import React, { useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { TableColumn } from 'tea-component';
import { useIntl, useSelector } from 'umi';

import { useLocal, useOption, useWeekDay, useWeekDayArr } from '@/pages/dashboard/utils/customHook';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import { commonDownloadValueFormatter, commonValueFormatter, formatFilterValues, getWeekDayTitle, handleDownload, unitConversion } from '@/pages/dashboard/utils/utils';
import CardBox from '@/pages/dashboard/components/CardBox';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import { addChartLabel, xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import { momentFormat } from '@/pages/dashboard/utils/_config/filtersTimeFormat';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import { getApiData } from '@/pages/dashboard/services/common';
import { dynamicString } from '@home/utils/dynamicString';
import dataCommonFormat from '@home/utils/dbDataFormat/dataCommonFormat';


const labelFormatX = (value) => (moment(value.split("(")[0]).format('MM-DD'));
const axisLabelFormat = (opt) => {
  return opt.xAxis?.map((item) => {
    return {
      ...item,
      axisLabel: {
        ...item.axisLabel,
        formatter: labelFormatX,
      },
    };
  });
};

function AveragePlaytimeTrendCo(props: any) {
  const { config: propsConfig, className, style, data } = props;
  const { api, componentMetrics, titleEn, titleCn, isMedian = false, metrics } = propsConfig || {};
  const contextObj = useContext(FilterContext);
  const { filterVal, filterLoading } = contextObj || {};
  const { gameCode } = useSelector((state: any) => {
    return {
      gameCode: state?.dashboard?.currentGame?.game_code,
    };
  });
  const { getWeekDay } = useWeekDay();
  const weekDayArr = useWeekDayArr();
  const { locale, isEN, getLocalText } = useLocal('engagement_active_online_avg_time');
  const [averageOnlineTimeData, setAverageOnlineTimeData] = useState([]);
  const [averageOnlineTimeLoading, setAverageOnlineTimeLoading] = useState(true);
  const [error, setError] = useState(false);
  const [iseye, setIseye] = useState(true);
  const { formatMessage } = useIntl(); // 中英切换方法
  const {
    doubleChartTitle,
    keyList,
  } = useMemo(() => {
    const doubleChartTitle: string[] = [];
    const keyList: string[] = [];
    componentMetrics?.map((item: any) => {
      const { metricKey, enName, cnName } = item || {};
      doubleChartTitle?.push(isEN ? dynamicString(enName, data) : dynamicString(cnName, data));
      keyList?.push(metricKey);
    });
    return {
      doubleChartTitle,
      keyList,
    };
  }, [componentMetrics, isEN, data]);

  // 创建支持 formatRule 的格式化函数
  const createValueFormatter = (metricConfig: any) => {
    return (value: any) => {
      const { type = 'int', decimal = 0, formatRule } = metricConfig || {};
      // 优先使用 formatRule（支持动态字符串）
      if (formatRule) {
        const resolvedFormatRule = dynamicString(formatRule, data) || '';
        if (resolvedFormatRule) {
          return dataCommonFormat({ value, formatRule: resolvedFormatRule });
        }
      }
      // 向后兼容：使用旧的 type + decimal 逻辑
      return commonValueFormatter(type, value, decimal);
    };
  };

  const leftValueFormatter = useMemo(() => createValueFormatter(componentMetrics?.[0]), [componentMetrics, data]);
  const rightValueFormatter = useMemo(() => createValueFormatter(componentMetrics?.[1]), [componentMetrics, data]);
  // 柱状图，线形图 浮窗
  const formatterFunc = (params) => {
    return getEchartTooltipText({
      title: `${params[0].axisValue}`,
      data: params.map(({ value, color }, index) => ({
        name: doubleChartTitle?.[index],
        metricsNum: index ? rightValueFormatter(value) : leftValueFormatter(value),
        color,
      })),
    });
  };

  useEffect(() => {
    if (!filterVal) return;
    !filterLoading && (isMedian ? getMedianData() : getData());
  }, [filterVal, filterLoading]);

  const getData = () => {
    const { rangeTime, diff, timeType, ...filters } = filterVal || {};
    const params: any = {
      game_code: gameCode,
      start_time: rangeTime?.[0] || '',
      end_time: rangeTime?.[1] || '',
      period: PERIODTIMEOBJ?.[timeType] || 'daily',
      filters: formatFilterValues(filters),
    };
    if (metrics) params.metrics = metrics;
    setAverageOnlineTimeLoading(true);
    getApiData(api, params).then((res: any) => {
      setAverageOnlineTimeLoading(false);
      setError(false);
      setAverageOnlineTimeData(res?.aot_list ?? []);
    })
      .catch(() => {
        setError(true);
        setAverageOnlineTimeLoading(false);
        setAverageOnlineTimeData([]);
      });
  };

  const getMedianData = () => {
    const { timeType, diff, rangeTime, ...filters } = filterVal || {};
    const params: any = {
      game_code: gameCode,
      period: PERIODTIMEOBJ?.[timeType],
      filters: formatFilterValues(filters),
      start_time: rangeTime?.[0],
      end_time: rangeTime?.[1],
      metrics: keyList,
    };
    setAverageOnlineTimeLoading(true);
    getApiData(api, params).then((res: any) => {
      setAverageOnlineTimeLoading(false);
      setError(false);
      const newData = res?.map((item: any) => {
        const obj: any = { date: moment(item?.date)?.format('YYYY-MM-DD') };
        Object.keys(item?.metrics)?.map((v: any) => {
          obj[v] = item?.metrics?.[v]?.value;
        });
        return obj;
      }) || [];
      setAverageOnlineTimeData(newData || []);
    })
      .catch(() => {
        setError(true);
        setAverageOnlineTimeLoading(false);
        setAverageOnlineTimeData([]);
      });
  };

  // 双轴自定义配置
  const config = useMemo(() => {
    const titleObj = {
      show: true,
      textStyle: {
        color: '#000',
        opacity: 0.65,
        fontSize: 12,
        fontWeight: 400,
      },
    };
    const titles = doubleChartTitle.reduce((pre, cur, i) => {
      if (!i) {
        pre.push({
          ...titleObj,
          textAlign: 'left',
          text: cur,
          left: -4,
        });
      } else {
        pre.push({
          ...titleObj,
          text: cur,
          right: 0,
        });
      }
      return pre;
    }, [] as any[]);
    const series = (type1 = 'bar', type2 = 'line') =>
      doubleChartTitle.reduce((pre, cur, i) => {
        if (!i) {
          pre.push({
            name: cur,
            type: type1,
            barMaxWidth: 80,
            barCategoryGap: '50%',
            symbol: 'circle',
            data: averageOnlineTimeData.map((el: any) => el?.[keyList?.[i]]),
            yAxisIndex: 0,
          });
        } else {
          pre.push({
            name: cur,
            type: type2,
            symbol: 'circle',
            barMaxWidth: 80,
            data: averageOnlineTimeData.map((el: any) => el?.[keyList?.[i]]),
            yAxisIndex: 1,
          });
        }
        return pre;
      }, [] as any[]);
    const grid = {
      x: 10,
      y: 40,
      x2: 10,
      y2: 30,
      containLabel: true
    };
    const yAxis: any[] = [
      {
        type: 'value',
        position: 'left',
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#ddd',
          },
        },
        axisLabel: {
          color: '#888888',
          fontSize: 12,
          formatter (value) {
            return unitConversion(value?.toFixed(2), true);
          },
        },
      },
    ];
    if (doubleChartTitle.length > 1) {
      yAxis.push({
        type: 'value',
        position: 'right',
        alignTicks: true, // 多Y轴时，开启是否对齐刻度线
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#ddd',
          },
        },
        axisLabel: {
          formatter (value) {
            return unitConversion(value?.toFixed(2), true);
          },
        },
      });
    }

    return {
      bar: {
        formatterFunc,
        setOtherConfigFunc: (opt) => {
          opt.xAxis = axisLabelFormat(opt);
          opt = {
            ...opt,
            title: titles,
            series: series('bar', 'line', false),
            yAxis,
            grid,
          };
          return opt;
        },
      },
      line: {
        formatterFunc,
        setOtherConfigFunc: (opt) => {
          opt.xAxis = axisLabelFormat(opt);
          opt = {
            ...opt,
            title: titles,
            series: series('line', 'line', false),
            yAxis,
            grid,
          };
          return opt;
        },
      },
    };
  }, [averageOnlineTimeData, doubleChartTitle, locale]);

  // 折线图 && 表格
  const option = useMemo(() => {
    // 双轴配置数据（柱状图双轴得自定义配置）
    const chart = doubleChartTitle.reduce((pre, name, index) => {
      if (averageOnlineTimeData?.length) {
        const chartData = {
          name,
          // type: index ? 'line' : 'bar',
          data: averageOnlineTimeData?.map((item: any) => {
            const key = keyList?.[index];

            return {
              x: getWeekDayTitle(item?.date, weekDayArr),
              y: item?.[key],
            };
          }),
        };
        pre.push(chartData);
      }
      return pre;
    }, [] as any[]);

    // 表头
    const tabbleOption: TableColumn[] = [
      {
        key: 'date',
        align: 'left',
        fixed: 'left',
        width: 100,
        header: getLocalText('date'),
        render: (cvm) => {
          const weekDay = getWeekDay(cvm?.date);
          const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
          return <>
            <span className='table-text'>{cvm?.date}</span>
            {filterVal?.timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
          </>;
        },
      },
    ];
    const metricColumnKeys = ['left', 'right'].slice(0, doubleChartTitle.length);
    const valueFormatters = [leftValueFormatter, rightValueFormatter];
    // 表头非固定列
    metricColumnKeys.reduce((pre: any, key, index) => {
      pre.push({
        key,
        align: 'right',
        width: 100,
        header: doubleChartTitle[index],
        render: (cvm) => <span>{cvm[key]}</span>,
      });
      return pre;
    }, tabbleOption);
    const tabbleData = averageOnlineTimeData?.reduce((outPre, outCur) => {
      const row: any = { date: outCur?.date };
      metricColumnKeys.forEach((key, index) => {
        row[key] = valueFormatters[index](outCur?.[keyList?.[index]]);
      });
      outPre.push(row);
      return outPre;
    }, [] as any[]);
    const getDownloadValue = (metricConfig: any, value: any) => {
      if (metricConfig?.formatRule) {
        const resolvedFormatRule = dynamicString(metricConfig.formatRule, data) || '';
        if (resolvedFormatRule) {
          return dataCommonFormat({ value, formatRule: resolvedFormatRule });
        }
      }
      return commonDownloadValueFormatter(
        (metricConfig?.type || 'decimals'),
        value,
        (metricConfig?.decimal || 2),
      );
    };
    const tabbleDownloadData = averageOnlineTimeData?.reduce((outPre, outCur) => {
      const row: any = { date: outCur?.date };
      metricColumnKeys.forEach((key, index) => {
        row[key] = getDownloadValue(componentMetrics?.[index] || {}, outCur?.[keyList?.[index]]);
      });
      outPre.push(row);
      return outPre;
    }, [] as any[]);
    return {
      chart,
      table: {
        option: tabbleOption,
        data: tabbleData,
        downloadData: tabbleDownloadData,
      },
    };
  }, [averageOnlineTimeData, locale, componentMetrics, data, leftValueFormatter, rightValueFormatter]);
  const { xAxisData, legendData, lineSeries, barSeries, isNodata } = useOption(option);
  const renderList = [
    {
      type: 'eyes',
      svgType: iseye ? 'open-eyes' : 'eyes-closed',
      content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
      isHideCom: true,
    },
    {
      type: 'barChart',
      svgType: 'column-chart',
      content: formatMessage({ id: 'common_bar_chart' }),
      component: <BarChart
        height={340}
        legendData={xAxisData.length > 0 ? legendData : []}
        xAxisData={xAxisData || []}
        series={xAxisData.length > 0 ? barSeries : []}
        formatterFunc={config?.bar?.formatterFunc}
        setOtherConfigFunc={(opt: any) => {
          if (config?.bar?.setOtherConfigFunc) opt = config?.bar?.setOtherConfigFunc(opt);
          opt = xAxisHighLight(opt, {
            formatter: (params: { value: string; }) => {
              return momentFormat(params?.value.split("(")[0], 'MM-DD') ?? '';
            }
          });
          opt = addChartLabel(opt, {
            formatter: (params: { value: string | number; }) => {
              return leftValueFormatter(params?.value);
            },
            show: iseye,
            formatNum: (value: string | number) => {
              return leftValueFormatter(value);
            }
          });
          return opt;
        }}
      />
    },
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: 'common_line_chart' }),
      component: <LineChart
        height={340}
        legendData={xAxisData.length > 0 ? legendData : []}
        xAxisData={xAxisData || []}
        series={xAxisData.length > 0 ? lineSeries : []}
        formatterFunc={config?.line?.formatterFunc}
        setOtherConfigFunc={(opt: any) => {
          if (config?.line?.setOtherConfigFunc) opt = config?.line?.setOtherConfigFunc(opt);
          opt = addChartLabel(opt, {
            formatter: (params: { value: string | number; }) => {
              return leftValueFormatter(params?.value);
            },
            show: iseye,
            formatNum: (value: string | number) => {
              return leftValueFormatter(value);
            }
          });
          opt = xAxisHighLight(opt, {
            formatter: (params: { value: string; }) => {
              return momentFormat(params?.value.split("(")[0], 'MM-DD') ?? '';
            }
          });
          return opt;
        }}
      />
    },
    {
      type: 'sheet',
      svgType: 'sheet',
      content: formatMessage({ id: 'common_table' }),
      component: <MiniTable
        height={340}
        loading={false}
        tableOption={option?.table?.option}
        tableList={option?.table?.data}
      />
    },
  ];

  const title = isEN ? (dynamicString(titleEn, data)) : (dynamicString(titleCn, data));
  return (
    <div className={className} style={style}>
      <CardBox
        className='register-table'
        height={340}
        bordered={false}
        titleCom={title}
        renderList={renderList}
        defaultAvtive={doubleChartTitle.length > 1 ? 1 : 2}
        setCurrentDataType={(type: any) => {
          if (type === 'eyes') {
            setIseye(!iseye);
          }
        }}
        downloadClick={async () => {
          handleDownload(option?.table, title);
        }}
        isLoading={averageOnlineTimeLoading}
        isError={error}
        isNodata={isNodata}
      />
    </div>
  );
}

export default AveragePlaytimeTrendCo;